import { setValueLanguage } from 'helpers/language'
import { compare } from 'helpers/sorting'
import { MAINTENANCE_UNIT } from 'types'
import i18n from 'config/i18n'

import type { ILoaderOption } from 'types'
import type {
  IStockKeepingUnitForm,
  IStockKeepingUnitFormPayload,
  ICategoriesOption,
} from 'features/StockKeepingUnit'

export const formatForm = (
  schemaForm: IStockKeepingUnitForm,
  isEdit?: boolean,
): IStockKeepingUnitFormPayload => {
  const {
    brand,
    categories,
    costCurrency,
    costPrice,
    detail,
    maintenancePeriod,
    maintenanceUnit,
    name,
    saleCurrency,
    salePrice,
    skuCode,
    isActive,
  } = schemaForm

  const costPriceValue = Number(costPrice.replace(/,/g, ''))
  const salePriceValue = Number(salePrice.replace(/,/g, ''))

  const options: ILoaderOption[] = categories?.map(
    (item: ICategoriesOption) => {
      return {
        key: item.id || '',
        label: item.label || '',
        value: item.id || '',
      }
    },
  )

  const newCategories: ICategoriesOption[] = options
    ?.sort(compare)
    .map((item: ILoaderOption) => {
      if (item.key) {
        return {
          id: item.key,
        }
      }

      return { nameTh: item.label || '' }
    })

  const create: IStockKeepingUnitFormPayload = {
    brand: brand.trim(),
    code: skuCode.trim(),
    categories: newCategories,
    costCurrency: costCurrency.key,
    costPrice: costPriceValue,
    detail: detail.trim(),
    maintenancePeriod: Number(maintenancePeriod),
    maintenanceUnit: maintenanceUnit?.key || MAINTENANCE_UNIT.NONE,
    name: name.trim(),
    saleCurrency: saleCurrency.key,
    salePrice: salePriceValue,
    isActive,
  }

  const update: IStockKeepingUnitFormPayload = {
    name: name.trim(),
    brand: brand.trim(),
    code: skuCode.trim(),
    categories: newCategories,
    costCurrency: costCurrency.key,
    costPrice: costPriceValue,
    detail: detail.trim(),
    maintenancePeriod: Number(maintenancePeriod),
    maintenanceUnit: maintenanceUnit?.key || MAINTENANCE_UNIT.NONE,
    saleCurrency: saleCurrency.key,
    salePrice: salePriceValue,
    isActive,
  }

  return isEdit ? update : create
}

export const sortProductCategories = (
  categories?: ICategoriesOption[],
): string => {
  if (!categories) {
    return '-'
  }

  const categoriesTh = categories
    ?.map((item) => item.nameTh)
    .sort()
    .join(', ')
  const categoriesEn = categories
    ?.map((item) => item.nameEn)
    .sort()
    .join(', ')

  return setValueLanguage(categoriesTh, categoriesEn)
}

export const generateCategoryOption = (
  item: ICategoriesOption,
): ICategoriesOption & ILoaderOption => {
  let label = item.name

  if (item.nameTh || item.nameEn) {
    label = i18n.language === 'th' ? item.nameTh : item.nameEn
  }

  return {
    ...item,
    key: item.id || '',
    label: label || '',
    value: item.id || '',
  }
}

export const generateSelectedCategoriesOption = (
  categories?: ICategoriesOption[],
): ILoaderOption[] => {
  const getCategories = (categories?: ICategoriesOption[]) => {
    return categories?.map((item: ICategoriesOption) =>
      generateCategoryOption(item),
    )
  }

  const data = categories?.map((item: ICategoriesOption) =>
    generateCategoryOption(item),
  ) as ILoaderOption[]

  const _previousCategories = getCategories(categories) as ILoaderOption[]

  const additionField = data?.filter(
    (item1: ICategoriesOption) =>
      !_previousCategories?.some((item2) => {
        return item1.id === item2.key
      }),
  ) as ILoaderOption[]

  const mappingValue = _previousCategories?.sort(compare).concat(additionField)

  return mappingValue as ILoaderOption[]
}
